import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Popup, ZoomControl, Pane } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

import { useTheme } from '../../../context/ThemeContext';
import { useUser } from '../../../context/UserContext';
import { useMultiCommodityRegionsData } from '../../../utils/api-hooks/analytics';
import { useEventGeometry } from '../../../utils/api-hooks/eventGeometry';
import { useWeatherEvents } from '../../../utils/api-hooks/weather';

import ShipMarker, { voyageHasDelay, voyageIsAhead, currentVesselName } from '../tracking/ShipMarker';
import { isHubWaiting, hubFocusInStack, buildHubWaitingPanel, selectHubPanelMembers, resolvePinHit, resolveSearchHit, resolveSelectedProp, applySelectionDecision } from '../../../utils/hubWait';
import HubWaitingPanel from '../tracking/HubWaitingPanel';
import { groupPinStacks } from '../../../utils/pinStacks';
import ContainerSearch from './ContainerSearch';
import RouteLayer from '../tracking/RouteLayer';
import ShipmentCard from '../tracking/ShipmentCard';
import ModeShipmentLayer, { buildModeItems } from './ModeShipmentLayer';
import { modeRowKey } from '../../../utils/modeRows';
import { MapResizeHandler, MapClickHandler, ShipClickResolver, ShipMarkerPanes, DisableZoomHandlers, RegionZoomHandler, FocusRegionHandler, FocusShipmentHandler, MapCenterHandler } from './MapHelpers';
import { injectLeafletStyles } from './leafletStyles';
import {
  STATUS_COLORS,
  OPENWEATHERMAP_API_KEY,
  API_MODE,
  computeForecastMaxHours,
  regionMarkerId,
} from './weatherConstants';
import AnimatedWeatherLayer from './AnimatedWeatherLayer';
import HazardGeometryLayer, { FitHazardOnReady } from './HazardGeometryLayer';
import HazardCard from './HazardCard';
import HazardEventMarker from './HazardEventMarker';
import { useMapInsets } from './useMapInsets';
import { useHazardSelection } from './useHazardSelection';
import { hazardBounds } from '../../../utils/gdacs/hazardBounds';
import { shiftedStormBounds, regionPinPosition } from '../../../utils/gdacs/regionPinFrame';
import { pathUpdatedMsOf } from '../../../utils/gdacs/geometryQuery';
import { eventListRefetchInterval, shouldRevalidateEvents } from '../../../utils/gdacs/eventListPolicy';
import { hazardBiqSummary } from '../../../utils/gdacs/hazardBiqSummary';
import { resolveStormClass } from '../../../utils/gdacs/stormClass';
import { useBiqPageContext } from '../../../context/BiqPageContext';
import { timelineEpochMs, stormStateAt, staticStormState } from '../../../utils/gdacs/stormClock';
import { CARTO_LIGHT_URL, CARTO_DARK_URL } from '../../../constants/URLs';
import { RegionPopupContent } from './WeatherPopups';
import WeatherLayerLegend from './WeatherLayerLegend';
import StatusLegend from './StatusLegend';
import WeatherMapControls, { STATUS_OPTIONS, WEATHER_LAYERS, EventsDropdown, StatusDropdown, ChecklistDropdown } from '../home/WeatherMapControls';
import { countryLabel, countrySummary } from '../../../utils/countryFilters';
import WeatherMapTimeline from '../home/WeatherMapTimeline';
import LayerToggle from './LayerToggle';
import MapSelect from './MapSelect';
import '../tracking/trackingStyles.css';

import { Loader2 } from 'lucide-react';
import { createLeafletPinIcon } from './leafletPinIcon';
import { getHazardIcon } from './hazardIcon';
import { shipmentPinLatLng, pinStackKey, offsetDelayedFromNeighbors } from '../tracking/portCoords';
import { predictedEta, timelinePct } from '../../../utils/shipmentSchedule';

const ALL_STATUS_IDS = STATUS_OPTIONS.map((s) => s.id);
/** Pins within this distance (km) share one click and open the top-left list. ~0.018°. */
const STACK_OPEN_KM = 2;

// Default predicate; module-level so its identity is stable across renders.
const PASS = () => true;
// Stable default array identity.
const NO_COMMODITIES = [];

const UnifiedMapContainer = ({
  shipments = [],
  flights = [],
  trucks = [],
  layerMode = 'all',
  onLayerModeChange,
  selectedShipment: selectedShipmentProp,
  onShipmentSelect,
  commodities = NO_COMMODITIES,
  selectedCommodity = '',
  selectedStatuses = [],
  onStatusesChange = () => {},
  zoomCountry = '',
  zoomRegion = '',
  hoveredRegionId = null,
  focusedRegion = null,
  mobileControlsExpanded = false,
  originFilter = null,
  destFilter = null,
  modeFilter = null,
  seaOn = true,
  countryPredicate = PASS,
}) => {
  const { isDark } = useTheme();
  const { email } = useUser();
  const { t } = useTranslation();

  // Weather state
  const [activeLayer, setActiveLayer] = useState('temp');
  const [activeEventTypes, setActiveEventTypes] = useState([]);

  // Driven by the commodity selector: when a commodity is picked, fetch & show
  // regions for that one commodity. Otherwise fall back to the user's preferred
  // commodities (initial state on first visit, before any selection).
  const visibleCommodities = useMemo(
    () => (selectedCommodity ? [selectedCommodity] : commodities),
    [selectedCommodity, commodities],
  );

  // Derived from URL: when no status param, default to all (no silent filtering).
  const visibleStatuses = useMemo(
    () => (selectedStatuses && selectedStatuses.length > 0 ? selectedStatuses : ALL_STATUS_IDS),
    [selectedStatuses],
  );
  const [currentHour, setCurrentHour] = useState(0);
  // Bumped by every user scrub (slider drag / marker click), never by the play interval, so the storm glyph snaps.
  const [scrubRevision, setScrubRevision] = useState(0);
  const handleScrub = useCallback((hour) => {
    setScrubRevision((r) => r + 1);
    setCurrentHour(hour);
  }, []);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPausedForZoom, setIsPausedForZoom] = useState(false);
  const [isLayerLoading, setIsLayerLoading] = useState(false);
  const [playSpeed, setPlaySpeed] = useState(1500);
  const [containerDetailOpen, setContainerDetailOpen] = useState(false);
  const [stackPick, setStackPick] = useState(null);
  // Open "Waiting at {port}" panel: stable identity only (hub key, pin anchor, optional focused trip).
  const [hubPanel, setHubPanel] = useState(null);
  const [searchClearSeq, setSearchClearSeq] = useState(0);
  const [hoveredStackId, setHoveredStackId] = useState(null);
  const [hoveredShipment, setHoveredShipment] = useState(null);
  // Air and truck selection is independent of the sea card: clicking a plane
  // draws its arc without closing whatever vessel the user had open.
  const [selectedModeKey, setSelectedModeKey] = useState(null);
  // Plane/truck under the cursor, resolved geometrically by ShipClickResolver.
  const [hoverModeKey, setHoverModeKey] = useState(null);
  const [modePopupRequest, setModePopupRequest] = useState(null);
  const handleModeHit = useCallback((key) => {
    setSelectedModeKey(key);
    setModePopupRequest((r) => ({ key, n: (r?.n || 0) + 1 }));
  }, []);
  const airItems = useMemo(() => buildModeItems(flights, 'air'), [flights]);
  const truckItems = useMemo(() => buildModeItems(trucks, 'truck'), [trucks]);
  // An unchecked mode loses its selection and hover (and its popup with it).
  useEffect(() => {
    if (selectedModeKey && !airItems.some((i) => i.key === selectedModeKey) && !truckItems.some((i) => i.key === selectedModeKey)) {
      setSelectedModeKey(null);
    }
    if (hoverModeKey && !airItems.some((i) => i.key === hoverModeKey) && !truckItems.some((i) => i.key === hoverModeKey)) {
      setHoverModeKey(null);
    }
  }, [airItems, truckItems, selectedModeKey, hoverModeKey]);
  const modeHoverRows = useMemo(
    () => [...airItems, ...truckItems].map((i) => ({ key: i.key, position: i.pos, selected: i.key === selectedModeKey })),
    [airItems, truckItems, selectedModeKey],
  );
  // Hour 0 of the timeline is mount time.
  const [startDate] = useState(() => new Date());
  // The OpenWeather forecast end is anchored to a daily refresh, NOT to mount
  // time. We compute the actual playable horizon once at mount so the timeline
  // and play wrap stop precisely at the last bucket with real tile data.
  const [maxHours] = useState(() => computeForecastMaxHours(startDate));
  const playIntervalRef = useRef(null);
  const wasPlayingBeforeZoomRef = useRef(false);

  // Fetch weather data — driven by the commodity selector. When a commodity is
  // picked, fetch only that one's regions; otherwise fall back to all preferred.
  const { regions, isLoading: regionsLoading } = useMultiCommodityRegionsData(
    email,
    visibleCommodities,
    null,
  );
  // The refresh interval depends on the selected storm, which only exists further down: it goes through state.
  const [eventsRefetchMs, setEventsRefetchMs] = useState(false);
  const { events, refetch: refetchEvents, dataUpdatedAt: eventsUpdatedAt } = useWeatherEvents({ refetchInterval: eventsRefetchMs });

  // Inject Leaflet styles
  useEffect(() => {
    return injectLeafletStyles(isDark, 'leaflet-custom-styles-unified-map');
  }, [isDark]);

  // Weather animation controls
  const handlePlayPause = useCallback(() => {
    setIsPlaying(prev => !prev);
    setIsPausedForZoom(false);
  }, []);

  const handleZoomStart = useCallback(() => {
    if (isPlaying) {
      wasPlayingBeforeZoomRef.current = true;
      setIsPausedForZoom(true);
    }
  }, [isPlaying]);

  const handleTilesLoaded = useCallback(() => {
    if (wasPlayingBeforeZoomRef.current && isPausedForZoom) {
      setIsPausedForZoom(false);
      wasPlayingBeforeZoomRef.current = false;
    }
  }, [isPausedForZoom]);

  const handleLayerLoading = useCallback(() => setIsLayerLoading(true), []);
  const handleLayerReady = useCallback(() => setIsLayerLoading(false), []);

  // Play interval. Wrap at `maxHours` (the last bucket the API actually has
  // tiles for, derived from the daily OWM refresh boundary). Snapping back to
  // 0 keeps playback on the 3-hour bucket grid and reuses the already-cached
  // hour-0 tile from the previous loop, eliminating the visible layer-less
  // gap that mod-241 + a hardcoded 240 cap used to produce.
  useEffect(() => {
    const shouldAnimate = isPlaying && !isPausedForZoom;
    if (shouldAnimate) {
      playIntervalRef.current = setInterval(() => {
        setCurrentHour((prev) => {
          const step = API_MODE === 'paid' ? 3 : 1;
          const next = prev + step;
          return next > maxHours ? 0 : next;
        });
      }, playSpeed);
    } else {
      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current);
        playIntervalRef.current = null;
      }
    }
    return () => { if (playIntervalRef.current) clearInterval(playIntervalRef.current); };
  }, [isPlaying, isPausedForZoom, playSpeed, maxHours]);

  const pinRegions = regions;

  // Filter regions. Commodity comparison is case-insensitive: NestedCommoditySelector
  // emits lowercase, but `r.commodityKey` may inherit the casing from preferred_commodities.
  const filteredRegions = useMemo(() => {
    if (visibleCommodities.length === 0 || visibleStatuses.length === 0) return [];
    const allowedCommodities = new Set(
      visibleCommodities.map((c) => (c || '').toLowerCase()),
    );
    return pinRegions.filter(
      (r) =>
        allowedCommodities.has((r.commodityKey || '').toLowerCase()) &&
        visibleStatuses.includes(r.harvest_status) &&
        r.latitude != null &&
        r.longitude != null,
    );
  }, [pinRegions, visibleCommodities, visibleStatuses]);

  // Filter events
  const filteredEvents = useMemo(() => {
    if (activeEventTypes.length === 0) return [];
    return events.filter(e => activeEventTypes.includes(e.type));
  }, [events, activeEventTypes]);

  const regionPinIconCache = useMemo(() => new Map(), []);
  // Marker instances keyed by regionMarkerId — lets a clicked Harvest Alerts card open the popup.
  const regionMarkerRefs = useRef(new Map());

  const getRegionPinIcon = useCallback((status, fillOpacity, enlarged = false) => {
    const fillColor = STATUS_COLORS[status] || STATUS_COLORS.not_producing;
    const variant = status === 'not_producing' ? 'glass' : 'solid';
    const key = `region|${fillColor}|${fillOpacity}|${enlarged ? 'xl' : ''}|${variant}`;
    const cached = regionPinIconCache.get(key);
    if (cached) return cached;
    // We approximate the old fillOpacity by blending color into the map via SVG opacity.
    // Leaflet divIcon doesn't support "fillOpacity" directly, so we encode it in SVG.
    const icon = createLeafletPinIcon({
      fillColor,
      sizePx: enlarged ? 44 : 28,
      strokeWidth: enlarged ? 1.8 : 0.5,  // bolder outline when hovered so it stands out
      className: fillOpacity < 0.85 ? 'leaflet-pin-icon--muted' : '',
      variant,
    });
    regionPinIconCache.set(key, icon);
    return icon;
  }, [regionPinIconCache]);

  const handleEventTypeToggle = useCallback((eventType) => {
    setActiveEventTypes(prev =>
      // Param renamed `et` (was `t`) so it doesn't shadow the i18n `t` above
      // (ported from portrait-signage f1bca32's council-review fix).
      prev.includes(eventType) ? prev.filter((et) => et !== eventType) : [...prev, eventType]
    );
  }, []);

  // A country-filtered-out selection keeps its card open but gets no route,
  // port pins or fly-to.
  // Sea off: no selected vessel, card, route or re-inserted marker, even on the first paint.
  const selectedShipment = seaOn ? selectedShipmentProp : null;
  const visibleSelected = selectedShipment && countryPredicate(selectedShipment) ? selectedShipment : null;

  const markerShipments = useMemo(() => {
    const list = [...(shipments || [])];
    const sel = selectedShipment?.container_number;
    if (sel) {
      const id = String(sel).toUpperCase();
      if (
        !list.some((s) => String(s?.container_number || '').toUpperCase() === id)
        && countryPredicate(selectedShipment)
      ) {
        list.push(selectedShipment);
      }
    }
    return list;
  }, [shipments, selectedShipment, countryPredicate]);

  const stackedMarkers = useMemo(() => {
    const merged = new Map(groupPinStacks(markerShipments, shipmentPinLatLng, pinStackKey, STACK_OPEN_KM)
      .map(({ root, items }) => [`m:${root}`, items]));
    const out = [];
    merged.forEach((items, key) => {
      const delayed = items.filter((item) => voyageHasDelay(item.shipment));
      const ahead = items.filter((item) => !voyageHasDelay(item.shipment) && voyageIsAhead(item.shipment));
      const rest = items.filter((item) => !voyageHasDelay(item.shipment) && !voyageIsAhead(item.shipment));
      const ranked = [...delayed, ...ahead, ...rest];
      const representative = ranked[0];
      const under = null; // one map icon per stack; side list selects among members
      out.push({
        ...representative,
        key,
        stackSize: items.length,
        stack: items,
        under,
        position: representative.pin, // true AIS only — no spiderfy/nudge for stacks
      });
    });
    out.forEach((row) => {
      if (!voyageHasDelay(row.shipment)) return;
      const neighbors = out.filter((other) => other.key !== row.key).map((other) => other.pin);
      const pushed = offsetDelayedFromNeighbors(row.pin, neighbors);
      if (pushed !== row.pin) row.position = pushed;
    });
    return out;
  }, [markerShipments]);

  const handleShipHover = useCallback((row) => {
    setHoveredShipment(row?.shipment || null);
  }, []);

  const hoverPreviewShipment = useMemo(() => {
    const s = seaOn ? hoveredShipment : null;
    if (!s) return null;
    if (!(s.route_geojson || s.voyage_stops)) return null;
    if (!countryPredicate(s)) return null;
    const selectedId = String(visibleSelected?.container_number || '').trim();
    if (selectedId && String(s.container_number || '').trim() === selectedId) return null;
    return s;
  }, [hoveredShipment, visibleSelected, countryPredicate, seaOn]);

  // Stack-picker rows the Origin/Destination filter hides are dropped during
  // render; the picker is hidden below two remaining entries.
  const visibleStackPick = useMemo(() => {
    if (!stackPick || !seaOn) return null;
    const kept = stackPick.filter((item) => countryPredicate(item.shipment));
    return kept.length > 1 ? kept : null;
  }, [stackPick, countryPredicate, seaOn]);
  // Shipments arrive already filtered by commodity from MapPage (single source of truth).
  const showShipments = layerMode === 'all' || layerMode === 'shipments';
  // Panel members come from the CURRENT filtered list (country filters, Sea, layer), never from
  // stale stack objects or markerShipments.
  const hubMembers = useMemo(() => {
    if (!hubPanel || !seaOn || !showShipments) return null;
    return selectHubPanelMembers({
      shipments,
      hubKey: hubPanel.hubKey,
      predicate: countryPredicate,
      pinLatLng: shipmentPinLatLng,
      cellKey: pinStackKey,
      km: STACK_OPEN_KM,
    });
  }, [hubPanel, shipments, countryPredicate, seaOn, showShipments]);
  const hubPanelModel = useMemo(
    () => (hubMembers && hubMembers.hubCards.length ? buildHubWaitingPanel(hubMembers.hubCards, hubMembers.others, hubMembers.otherHubCards) : null),
    [hubMembers],
  );
  // One place applies a selection transition (see applySelectionDecision); the card is the parent's prop.
  const commit = useCallback((decision) => {
    if (decision.kind === 'none') return; // writing the render's stale state would undo a queued setHubPanel(null)
    const next = applySelectionDecision(
      { hubPanel, stackPick, hoveredStackId, selectedShipment: selectedShipmentProp }, decision,
    );
    setHubPanel(next.hubPanel);
    setStackPick(next.stackPick);
    setHoveredStackId(next.hoveredStackId);
    if (next.clearSearch) setSearchClearSeq((n) => n + 1);
    if (next.selectedShipment !== selectedShipmentProp) onShipmentSelect(next.selectedShipment);
  }, [hubPanel, stackPick, hoveredStackId, selectedShipmentProp, onShipmentSelect]);
  // Filters removed every hub box: close the panel.
  useEffect(() => {
    if (!hubPanel || hubPanelModel) return;
    setHubPanel(null);
  }, [hubPanel, hubPanelModel]);
  const closeHubPanel = useCallback(() => setHubPanel(null), []);
  // A hub card handed in as the selection (prop-driven) becomes the panel, never a ShipmentCard.
  useEffect(() => {
    commit(resolveSelectedProp({
      selected: selectedShipmentProp, shipments, predicate: countryPredicate, enabled: seaOn && showShipments,
    }));
  }, [selectedShipmentProp, shipments, countryPredicate, seaOn, showShipments]);
  // A stale picker must not survive Sea being unchecked (nor resurrect a ship later).
  useEffect(() => {
    if (!seaOn) { setStackPick(null); setHoveredStackId(null); setHoveredShipment(null); }
  }, [seaOn]);


  // Origin / Destination checklist. All options selected -> "All"; otherwise
  // names on >=sm (first 2 + "+N") and a count below sm.
  const renderCountryDropdown = (label, filter, align) => {
    if (!filter) return null;
    const { keys, selectedKeys, mode, onChange, onSelectAll } = filter;
    const n = selectedKeys.length;
    const text = countrySummary(selectedKeys, mode);
    const summary = text.kind === 'names' ? (
      <>
        <span className="hidden sm:inline">{text.names}</span>
        <span className="sm:hidden">({n})</span>
      </>
    ) : t(`map.filters.${text.kind}`, text.kind === 'all' ? 'All' : 'None');
    return (
      <div className="rounded-[8px] bg-white shadow-sm dark:bg-[#1d1d1d]">
      <ChecklistDropdown
        variant="stacked"
        label={label}
        count={n}
        summary={summary}
        allActive={mode === 'all'}
        align={align}
        clearLabel={t('map.filters.clear', 'Clear')}
        listClassName="max-h-[min(320px,40svh)] overflow-y-auto"
        items={keys.map((k) => ({ id: k, label: countryLabel(k) }))}
        selectedIds={selectedKeys}
        onToggle={(id) => onChange(selectedKeys.includes(id) ? selectedKeys.filter((k) => k !== id) : [...selectedKeys, id])}
        onSelectAll={onSelectAll}
        onSelectNone={() => onChange([])}
        searchable
      />
      </div>
    );
  };
  // Transport-mode checklist (Sea / Air / Truck).
  const renderModeDropdown = () => {
    if (!modeFilter) return null;
    const { keys, selectedKeys, mode, onChange, onSelectAll } = modeFilter;
    const label = (k) => t(`map.filters.${k}`, k.charAt(0).toUpperCase() + k.slice(1));
    const n = selectedKeys.length;
    const summary = mode === 'all' ? t('map.filters.all', 'All')
      : n === 0 ? t('map.filters.none', 'None')
        : (
          <>
            <span className="hidden sm:inline">{selectedKeys.map(label).join(', ')}</span>
            <span className="sm:hidden">({n})</span>
          </>
        );
    return (
      <div className="rounded-[8px] bg-white shadow-sm dark:bg-[#1d1d1d]">
        <ChecklistDropdown
          variant="stacked"
          label={t('map.filters.mode', 'Mode')}
          count={n}
          summary={summary}
          allActive={mode === 'all'}
          align="left"
          clearLabel={t('map.filters.clear', 'Clear')}
          items={keys.map((k) => ({ id: k, label: label(k) }))}
          selectedIds={selectedKeys}
          onToggle={(id) => onChange(selectedKeys.includes(id) ? selectedKeys.filter((k) => k !== id) : [...selectedKeys, id])}
          onSelectAll={onSelectAll}
          onSelectNone={() => onChange([])}
        />
      </div>
    );
  };
  const showWeather = layerMode === 'all' || layerMode === 'weather';
  const weatherOpacity = 0.8;
  const hasApiKey = !!OPENWEATHERMAP_API_KEY;

  // Hazard detail: one selected event, its lazily fetched footprint, the pins inside it and the
  // storm position for the shared timeline clock.
  const { selectedEvent, selectEvent, clearSelection } = useHazardSelection({
    events,
    activeEventTypes,
    enabled: showWeather,
  });
  // Event list: revalidated when a live cyclone is opened (if older than 5 min) and polled while one stays selected.
  useEffect(() => { setEventsRefetchMs(eventListRefetchInterval(selectedEvent)); }, [selectedEvent]);
  useEffect(() => {
    if (shouldRevalidateEvents({ event: selectedEvent, dataUpdatedAt: eventsUpdatedAt, nowMs: Date.now() })) refetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent?.id]);
  const {
    parsed, status: geometryStatus, meta: pathMetaState, refetch: refetchGeometry
  } = useEventGeometry(selectedEvent);
  // Exposure checks ALL preferred commodities (not just the one picked in the selector).
  const clockMs = useMemo(() => timelineEpochMs(startDate, currentHour), [startDate, currentHour]);
  const pathUpdatedMs = pathUpdatedMsOf(parsed);
  // The selected hazard goes to BiQ as page context (its own slot, so the page's extras are never overwritten).
  const { setPageHazard } = useBiqPageContext() || {};
  const classWords = resolveStormClass({ text: selectedEvent?.severityData?.text, parsed, lng: selectedEvent?.lng });
  useEffect(() => {
    if (!setPageHazard) return undefined;
    setPageHazard(selectedEvent ? hazardBiqSummary({ event: selectedEvent, classWords, status: geometryStatus }) : null);
    return () => setPageHazard(null);
  }, [setPageHazard, selectedEvent, classWords, geometryStatus]);
  const selectedIsCurrent = !!selectedEvent?.isCurrent;
  const clockState = useMemo(() => {
    if (!parsed || parsed.eventType !== 'TC') return null;
    return selectedIsCurrent ? stormStateAt(parsed, clockMs) : staticStormState(parsed);
  }, [parsed, clockMs, selectedIsCurrent]);
  // Free rectangle of the map (outside the filter strip, legends and the storm card) for fits.
  const { insets, setWrapperEl, setTopEl, setBottomEl, setPanelEl } = useMapInsets();
  const [fitNonce, setFitNonce] = useState(0);
  const handleSelectHazard = useCallback((e) => {
    selectEvent(e);
    setFitNonce((n) => n + 1);
  }, [selectEvent]);
  const hazardDetail = useMemo(() => (selectedEvent ? {
    parsed, status: geometryStatus, clockState, clockMs, pathUpdatedMs, pathMeta: pathMetaState, onRetry: refetchGeometry,
  } : null), [selectedEvent, parsed, geometryStatus, clockState, clockMs, pathUpdatedMs, pathMetaState, refetchGeometry]);
  // The storm card shares the left edge with the shipment card and the hub panel: only one of them is open at a time.
  useEffect(() => {
    if (!selectedEvent) return;
    if (selectedShipmentProp) onShipmentSelect(null);
    if (hubPanel) setHubPanel(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent?.id]);
  useEffect(() => {
    if (selectedEvent && (selectedShipmentProp || hubPanel)) clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShipmentProp, hubPanel]);
  const hazardFitBounds = useMemo(() => (parsed ? hazardBounds(parsed) : null), [parsed]);
  const stormPinBounds = useMemo(() => shiftedStormBounds(parsed), [parsed]);
  const hazardLabels = useMemo(() => ({
    shortClasses: {
      HU: t('map.hazard.shortClasses.HU', 'H'),
      TS: t('map.hazard.shortClasses.TS', 'TS'),
      TD: t('map.hazard.shortClasses.TD', 'TD'),
    },
  }), [t]);


  // Portrait/mobile (e.g. signage panel, <1024px): center on North America so
  // the tall frame frames the Americas well. Landscape/desktop and portrait
  // *monitors* (>=1024px) keep the original center. Ported from
  // portrait-signage 18a161b + d1e391d's council-review width gate.
  //
  // Tracked as state (not read once) and kept in sync with a matchMedia
  // listener so rotating/resizing an already-mounted device re-centers the
  // map — `MapContainer`'s `center` prop only applies on initial mount, so
  // `MapCenterHandler` below calls `setView` whenever `mapCenter` changes.
  const [isPortraitViewport, setIsPortraitViewport] = useState(
    () => typeof window !== 'undefined'
      && !!window.matchMedia?.('(orientation: portrait) and (max-width: 1023px)').matches
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(orientation: portrait) and (max-width: 1023px)');
    const handler = (e) => setIsPortraitViewport(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const mapCenter = useMemo(
    () => (isPortraitViewport ? [40, -100] : [15, -60]),
    [isPortraitViewport]
  );

  return (
    <div className="relative w-full flex flex-col max-lg:h-auto lg:h-full">
      {/* Weather controls bar — only when weather visible.
          Commodity filter lives in GlobalFilterStrip (above ScorecardsBar) on
          both breakpoints; we hide it here to avoid duplication. Status
          filter lives there too on desktop only — on mobile it moved into
          this component's own "Map controls" panel below. */}
      {/* Mobile (<lg): "Map controls" content — the LayerToggle (what to
          display: All/Shipments/Weather) plus, when weather is visible, an
          app-styled MapSelect for the weather layer, the Status filter
          (moved here from GlobalFilterStrip's top strip on mobile only —
          see GlobalFilterStrip's `hidden lg:block` gate around its own
          StatusDropdown), and a bordered EventsDropdown (cyclone/flood/
          earthquake as a checklist). Expand/collapse is driven by the "Map
          controls" pill in ScorecardsBar (Option B) via the
          `mobileControlsExpanded` prop lifted to MapPage — no in-place
          header toggle here. Gated ONLY on `mobileControlsExpanded` (not
          `showWeather`) so the LayerToggle stays reachable in Shipments
          mode, when weather is off. `relative isolate z-[1200]` elevates
          this block's own stacking context above the Leaflet map sibling
          below (map is `z-0 isolate`, so a menu z-index alone can't beat
          it) — same pattern as the scorecards unit dropdown (z-[1250]). */}
      {mobileControlsExpanded && (
        <div className="relative isolate z-[1200] mx-2 my-1 lg:hidden">
          <div className="pb-1.5">
            <LayerToggle variant="inline" layerMode={layerMode} onModeChange={onLayerModeChange} />
          </div>
          {showWeather && (
            <div className="flex flex-wrap items-center gap-2 pb-1.5">
              <MapSelect
                value={activeLayer}
                options={WEATHER_LAYERS.map((l) => ({ value: l.id, label: t(`map.layers.${l.id}`, l.label) }))}
                onSelect={setActiveLayer}
                ariaLabel={t('map.controls.weatherLayerAria', 'Map weather layer')}
                className="flex-1 min-w-[110px]"
              />
              <StatusDropdown
                regions={regions}
                visibleStatuses={visibleStatuses}
                onStatusesChange={onStatusesChange}
                variant="unified"
              />
              <EventsDropdown activeTypes={activeEventTypes} onToggle={handleEventTypeToggle} variant="unified" />
            </div>
          )}
        </div>
      )}
      {/* Desktop (lg+): unchanged — WeatherMapControls card, weather-only. */}
      {showWeather && (
        <div className="hidden lg:block">
          <WeatherMapControls
            commodities={commodities}
            regions={regions}
            visibleCommodities={visibleCommodities}
            visibleStatuses={visibleStatuses}
            onCommoditiesChange={() => {}}
            onStatusesChange={() => {}}
            hideFilters
            variant="card"
            countryOptions={[]}
            selectedCountry=""
            onCountryChange={() => {}}
            regionOptions={[]}
            selectedRegion=""
            onRegionChange={() => {}}
            activeLayer={activeLayer}
            onLayerChange={setActiveLayer}
            activeEventTypes={activeEventTypes}
            onEventTypeToggle={handleEventTypeToggle}
          />
        </div>
      )}

      {/* Map. `z-0 isolate` contains Leaflet's internal z-indexes (panes ~200-700,
          controls ~800-1000) inside this stacking context — the same pattern the
          Home (WeatherMap) and Analytics (ProducingRegionsMap) maps use — so the
          map never paints over the page chrome or the nav drawer. */}
      <div ref={setWrapperEl} className="relative z-0 isolate max-lg:h-[70svh] max-lg:min-h-[360px] max-lg:flex-none lg:flex-1 lg:min-h-0">
        {/* Layer toggle — desktop-only floating overlay; mobile uses the
            inline variant inside the "Map controls" panel above. */}
        <div className="max-lg:hidden">
          <LayerToggle layerMode={layerMode} onModeChange={onLayerModeChange} />
        </div>

        {/* Container search (left column). Commodity filter moved to GlobalFilterStrip.
            `left-[52px]` on every breakpoint clears the Leaflet zoom control,
            which is pinned topleft with a 12px margin and 28px buttons
            (12 + 28 + 12 = 52) — at the old mobile `left-2` the search box sat
            straight on top of the +/− buttons. */}
        {showShipments && visibleStackPick && visibleStackPick.length > 1 && (
          <div
            className={`absolute top-[52px] left-[52px] z-[1100] w-[280px] max-h-[320px] overflow-y-auto rounded-lg shadow-lg border text-xs ${
            isDark ? 'bg-[#1d1d1d] border-[#333] text-gray-200' : 'bg-white border-[#e5e7eb] text-[#1f2937]'
          }`}>
            <div className={`px-2.5 py-1.5 font-semibold border-b flex items-center justify-between gap-2 ${isDark ? 'border-[#333]' : 'border-[#e5e7eb]'}`}>
              <span className="min-w-0">
                {(() => {
                  const keys = visibleStackPick.map((item) => String(
                    item.shipment?.current_vessel || item.shipment?.vessel || '',
                  ).toUpperCase().trim());
                  const sameVessel = keys.length > 0 && keys.every((k) => k && k === keys[0]);
                  return sameVessel
                    ? t('seaTracking.card.stackShipments', '{{count}} shipments at this point', { count: visibleStackPick.length })
                    : t('seaTracking.card.stackShips', '{{count}} ships at this point', { count: visibleStackPick.length });
                })()}
              </span>
              <button
                type="button"
                aria-label={t('common.close')}
                onClick={() => { setHoveredStackId(null); setStackPick(null); }}
                className={`shrink-0 text-base leading-none px-1 rounded ${isDark ? 'text-gray-300 hover:bg-[#333]' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                ×
              </button>
            </div>
            {(() => {
            const renderRow = (item) => {
              const s = item.shipment;
              const delayed = voyageHasDelay(s);
              const ahead = !delayed && voyageIsAhead(s);
              const id = String(s.container_number || '');
              const isHover = Boolean(hoveredStackId) && String(hoveredStackId) === id;
              const isSel = isHover || (
                !hoveredStackId
                && visibleSelected?.container_number
                && String(visibleSelected.container_number) === id
              );
              const rowBg = delayed
                ? (isDark ? 'bg-red-950/50' : 'bg-red-50')
                : ahead
                  ? (isDark ? 'bg-green-950/50' : 'bg-green-50')
                  : (isDark ? 'bg-[#1d1d1d]' : 'bg-white');
              const hoverBg = delayed
                ? (isDark
                  ? 'hover:bg-red-900 hover:ring-2 hover:ring-inset hover:ring-red-400 active:bg-red-800'
                  : 'hover:bg-red-200 hover:ring-2 hover:ring-inset hover:ring-red-600 active:bg-red-300')
                : ahead
                  ? (isDark
                    ? 'hover:bg-green-900 hover:ring-2 hover:ring-inset hover:ring-green-400 active:bg-green-800'
                    : 'hover:bg-green-200 hover:ring-2 hover:ring-inset hover:ring-green-700 active:bg-green-300')
                  : (isDark
                    ? 'hover:bg-[#3a3a3a] hover:ring-2 hover:ring-inset hover:ring-gray-300 active:bg-[#4a4a4a]'
                    : 'hover:bg-gray-200 hover:ring-2 hover:ring-inset hover:ring-gray-800 active:bg-gray-300');
              const selBg = isSel
                ? (isDark ? 'ring-2 ring-inset ring-white bg-[#333]' : 'ring-2 ring-inset ring-gray-900 bg-gray-100')
                : '';
              const titleCls = delayed
                ? (isDark ? 'text-red-400' : 'text-red-700')
                : ahead
                  ? (isDark ? 'text-green-400' : 'text-green-700')
                  : (isDark ? 'text-gray-100' : 'text-[#111827]');
              const boxes = Number(s.total_containers);
              const boxLabel = Number.isFinite(boxes) && boxes > 0
                ? ` · ${boxes} box${boxes === 1 ? '' : 'es'}`
                : '';
              return (
                <button
                  key={s.container_number}
                  type="button"
                  onMouseEnter={() => setHoveredStackId(s.container_number)}
                  onFocus={() => setHoveredStackId(s.container_number)}
                  onClick={() => {
                    commit(resolvePinHit([{ shipment: s }]));
                  }}
                  className={`w-full text-left px-2.5 py-1.5 ${rowBg} ${hoverBg} ${selBg}`}
                >
                  <div className={`font-semibold truncate ${titleCls}`}>{currentVesselName(s) || s.container_number}</div>
                  <div className={isDark ? 'text-gray-400' : 'text-[#6b7280]'}>
                    {s.arrival_port || '—'}{boxLabel}
                  </div>
                </button>
              );
            };
            return visibleStackPick.map((item) => renderRow(item));
            })()}
          </div>
        )}
        {/* Controls column: ONE controls row (search + filters), then the waiting panel beneath it.
            The column is click-through; only the controls and the panel take pointer events, so the
            map under the empty parts of the column still pans and clicks. */}
        <div className="pointer-events-none absolute top-[12px] left-[52px] z-[1000] flex max-h-[calc(100%-24px)] max-w-[calc(100%-64px)] lg:max-w-[calc(100%-290px)] flex-col items-start gap-2">
          <div ref={setTopEl} className="pointer-events-none flex max-w-full shrink-0 flex-wrap items-start gap-2 [&>*]:pointer-events-auto">
          {showShipments && seaOn && (
            <ContainerSearch
              isDark={isDark}
              activeShipments={shipments}
              clearSignal={searchClearSeq}
              onResultSelect={(shipmentResult) => {
                if (!shipmentResult) return;
                const fromActive = shipments.find((s) =>
                  s?.container_number && shipmentResult.container_number
                  && String(s.container_number).toUpperCase() === String(shipmentResult.container_number).toUpperCase()
                );
                const d = resolveSearchHit({
                  result: shipmentResult, fromActive, shipments, predicate: countryPredicate,
                });
                commit(d.kind === 'card' ? { ...d, keepPicker: true } : d);
                // A hub result opens the panel, so the search detail panel must not cover it.
                return d.kind === 'panel';
              }}
              onDetailPanelChange={setContainerDetailOpen}
            />
          )}
          {/* Origin then Destination: shipment filters, so only on the layers that
              draw shipments (All, Shipments), not on the weather-only layer. */}
          {showShipments && (
            <div className="flex min-w-0 max-w-full flex-wrap items-start gap-2">
              {renderCountryDropdown(t('map.filters.origin', 'Origin'), originFilter, 'left')}
              {renderCountryDropdown(t('map.filters.destination', 'Destination'), destFilter, 'right')}
              {renderModeDropdown()}
            </div>
          )}
          </div>
          {hubPanelModel && (
            <HubWaitingPanel
              panel={hubPanelModel}
              focusTripKey={hubPanel?.focusTripKey || null}
              focusSeq={hubPanel?.seq || 0}
              isDark={isDark}
              onClose={closeHubPanel}
              onSelectShipment={(sh) => commit({ kind: 'card', shipment: sh })}
              onSelectHub={(h) => commit({ kind: 'panel', hubKey: h.hubKey })}
            />
          )}
        </div>

        {/* Loading indicator */}
        {isLayerLoading && showWeather && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] px-3 py-1.5 rounded-full bg-bloom-green/90 text-white flex items-center gap-2 shadow-lg text-xs">
            <Loader2 size={14} className="animate-spin" />
            <span>Loading {WEATHER_LAYERS.find(l => l.id === activeLayer)?.label || 'layer'}...</span>
          </div>
        )}

        <MapContainer
          center={mapCenter}
          zoom={3}
          className="produce-map-controls"
          style={{ height: '100%', width: '100%', background: isDark ? '#1d1d1d' : '#f3f4f6' }}
          zoomControl={false}
          scrollWheelZoom={false}
          doubleClickZoom={false}
          touchZoom={false}
          boxZoom={false}
        >
          <ShipMarkerPanes />
          <FitHazardOnReady bounds={hazardFitBounds} ready={geometryStatus === 'ready'} selectedId={selectedEvent?.id ?? null} fitNonce={fitNonce} insets={insets} />
          <MapClickHandler onMapClick={clearSelection} />
          <ZoomControl position="topleft" />
          <MapCenterHandler
            center={mapCenter}
            hasLocationTarget={!!(zoomCountry || zoomRegion || focusedRegion)}
          />
          <FocusShipmentHandler shipment={visibleSelected} />
          <DisableZoomHandlers />
          <MapResizeHandler />
          <ShipClickResolver
            rows={showShipments ? stackedMarkers : []}
            modeRows={showShipments ? modeHoverRows : null}
            onModeHoverChange={setHoverModeKey}
            onModeHit={handleModeHit}
            onHoverChange={handleShipHover}
            onHit={(row) => {
              const d = resolvePinHit(row.stack);
              commit(d.kind === 'card' ? { ...d, keepHover: true } : d);
            }}
            onMiss={() => commit({ kind: 'miss' })}
          />
          <RegionZoomHandler regions={regions} country={zoomCountry} region={zoomRegion} />

          <TileLayer
            url={isDark ? CARTO_DARK_URL : CARTO_LIGHT_URL}
            attribution='&copy; <a href="https://carto.com/">CARTO</a>'
          />

          {/* Weather tile layer — always mounted, visibility controlled via prop */}
          {hasApiKey && (
            <AnimatedWeatherLayer
              activeLayer={activeLayer}
              currentHour={currentHour}
              startDate={startDate}
              apiMode={API_MODE}
              maxHours={maxHours}
              isDark={isDark}
              opacity={weatherOpacity}
              visible={showWeather}
              isPlaying={isPlaying}
              onZoomStart={handleZoomStart}
              onTilesLoaded={handleTilesLoaded}
              onLayerLoading={handleLayerLoading}
              onLayerReady={handleLayerReady}
            />
          )}

          <HazardGeometryLayer
            parsed={parsed}
            clockState={clockState}
            clockMs={clockMs}
            isPlaying={isPlaying}
            playSpeed={playSpeed}
            isDark={isDark}
            isCurrent={selectedIsCurrent}
            scrubRevision={scrubRevision}
            labels={hazardLabels}
          />

          {/* Region markers */}
          {showWeather && filteredRegions.map((region, idx) => {
            const enlarged = hoveredRegionId != null && regionMarkerId(region) === hoveredRegionId;
            return (
            <Marker
              key={`region-${region.commodityKey}-${region.specific_location || region.region}-${idx}`}
              position={regionPinPosition(region, stormPinBounds)}
              icon={getRegionPinIcon(region.harvest_status, layerMode === 'all' ? 0.6 : 0.85, enlarged)}
              zIndexOffset={enlarged ? 1000 : 0}
              ref={(m) => {
                const id = regionMarkerId(region);
                if (m) regionMarkerRefs.current.set(id, m);
                else regionMarkerRefs.current.delete(id);
              }}
              eventHandlers={{ click: (e) => e.target.openPopup() }}
            >
              <Popup>
                <RegionPopupContent region={region} isDark={isDark} />
              </Popup>
            </Marker>
            );
          })}

          {/* After the pins, so their position effects run first and focus reads the drawn position. */}
          <FocusRegionHandler focusedRegion={focusedRegion} markerRefs={regionMarkerRefs} />

          {/* Weather event markers: own pane above the ship panes (and below popups) */}
          <Pane name="hazards" style={{ zIndex: 695 }}>
            {showWeather && filteredEvents.map((event, idx) => {
              const icon = getHazardIcon(event.type);
              if (!icon) return null;
              return (
                <HazardEventMarker
                  key={`event-${event.id || idx}`}
                  event={event}
                  icon={icon}
                  title={t(`map.events.${event.type}`, event.type)}
                  onSelect={handleSelectHazard}
                />
              );
            })}
          </Pane>

          {/* Ship markers */}
          {showShipments && stackedMarkers.map((row) => {
            const selectedId = String(visibleSelected?.container_number || '').trim();
            const hoveredId = String(hoveredStackId || '').trim();
            const matchId = (item, id) => id && String(item.shipment?.container_number || '').trim() === id;
            const focusItem = (hoveredId && row.stack.find((item) => matchId(item, hoveredId)))
              || (selectedId && row.stack.find((item) => matchId(item, selectedId)))
              || hubFocusInStack(row.stack, hubPanel)
              || null;
            const topItem = focusItem || { shipment: row.shipment, pin: row.position };
            const topId = String(topItem.shipment?.container_number || '');
            const ring = Boolean(focusItem);
            const topPin = row.position;
            const topDelayed = voyageHasDelay(topItem.shipment);
            const topAhead = !topDelayed && voyageIsAhead(topItem.shipment);
            return (
              <ShipMarker
                key={row.key}
                shipment={topItem.shipment}
                position={topPin}
                isSelected={ring}
                interactive={false}
                zIndexOffset={
                  topDelayed
                    ? 1200000
                    : topAhead
                      ? 400000
                      : ring
                        ? 200000
                        : 80000
                }
              />
            );
          })}

          {/* Air + truck layers. They follow the same showShipments
              toggle as vessels — the control is "shipments", not "sea". */}
          {showShipments && (
            <>
              <ModeShipmentLayer
                items={airItems}
                mode="air"
                selectedKey={selectedModeKey}
                hoverKey={hoverModeKey}
                popupRequest={modePopupRequest}
                onSelect={(row) => setSelectedModeKey(modeRowKey(row))}
              />
              <ModeShipmentLayer
                items={truckItems}
                mode="truck"
                selectedKey={selectedModeKey}
                hoverKey={hoverModeKey}
                popupRequest={modePopupRequest}
                onSelect={(row) => setSelectedModeKey(modeRowKey(row))}
              />
            </>
          )}

          {/* Route for selected shipment */}
          {showShipments && visibleSelected && (visibleSelected.route_geojson || visibleSelected.voyage_stops) && (
            <RouteLayer
              key={`route-${visibleSelected.container_number}-${voyageHasDelay(visibleSelected) ? 'D' : 'OK'}`}
              shipment={visibleSelected}
              routeGeojson={visibleSelected.route_geojson}
              departurePort={visibleSelected.departure_port}
              arrivalPort={visibleSelected.arrival_port}
              voyageStops={visibleSelected.voyage_stops}
              hasShipsgoCurrent={visibleSelected.has_shipsgo_current}
              vessel={visibleSelected.vessel}
              currentVessel={visibleSelected.current_vessel}
              delayDays={visibleSelected.delay_days}
              rowIsDelayed={voyageHasDelay(visibleSelected)}
            />
          )}

          {/* Hover preview: path only */}
          {showShipments && hoverPreviewShipment && (
            <RouteLayer
              key={`hover-route-${hoverPreviewShipment.container_number}`}
              preview
              shipment={hoverPreviewShipment}
              routeGeojson={hoverPreviewShipment.route_geojson}
              departurePort={hoverPreviewShipment.departure_port}
              arrivalPort={hoverPreviewShipment.arrival_port}
              voyageStops={hoverPreviewShipment.voyage_stops}
              hasShipsgoCurrent={hoverPreviewShipment.has_shipsgo_current}
              vessel={hoverPreviewShipment.vessel}
              currentVessel={hoverPreviewShipment.current_vessel}
              delayDays={hoverPreviewShipment.delay_days}
              rowIsDelayed={voyageHasDelay(hoverPreviewShipment)}
            />
          )}
        </MapContainer>

        {selectedEvent && (
          <HazardCard
            event={selectedEvent}
            isDark={isDark}
            detail={hazardDetail}
            onClose={clearSelection}
            rootRef={setPanelEl}
          />
        )}

        {/* Legends — overlaid on the map (spec: "now located on a MAP, white
            panels a bit transparent") instead of a boxed row below it, which is
            what buys the map its extra height. `bottom-[26px]` clears Leaflet's
            attribution strip in the bottom-right corner. The wrapper is
            `pointer-events-none` so map dragging still works in the gap between
            the two panels, and sits at z-[900] so the draggable ShipmentCard
            (z-[1000]) stays on top. Below lg the two panels stack left-aligned;
            from lg they split to the two bottom corners as in the design. */}
        {showWeather && (
          <div ref={setBottomEl} className={`pointer-events-none absolute inset-x-[12px] bottom-[26px] z-[900] ${selectedEvent ? 'max-sm:invisible' : ''} flex flex-col items-start gap-[8px] lg:flex-row lg:items-end lg:justify-between`}>
            {hasApiKey ? (
              <div className="pointer-events-auto max-w-full lg:w-[320px]">
                <WeatherLayerLegend activeLayer={activeLayer} isDark={isDark} variant="overlay" />
              </div>
            ) : (
              <div />
            )}
            <div className="pointer-events-auto max-w-full lg:max-w-[60%]">
              <StatusLegend activeEventTypes={activeEventTypes} variant="overlay" />
            </div>
          </div>
        )}


        {/* Shipment card */}
        {showShipments && selectedShipment && !isHubWaiting(selectedShipment) && (
          <ShipmentCard
            shipment={selectedShipment}
            onClose={() => onShipmentSelect(null)}
            containerDetailOpen={containerDetailOpen}
          />
        )}

      </div>

      {/* Weather timeline */}
      {showWeather && hasApiKey && (
        <WeatherMapTimeline
          variant="card"
          currentHour={currentHour}
          isPlaying={isPlaying}
          isLoadingTiles={isPausedForZoom}
          playSpeed={playSpeed}
          onPlayPause={handlePlayPause}
          onTimeChange={handleScrub}
          onSpeedChange={setPlaySpeed}
          startDate={startDate}
          apiMode={API_MODE}
          maxHours={maxHours}
        />
      )}
    </div>
  );
};

export default UnifiedMapContainer;
